/**
 * Admin Shell — single top bar + content area (login redesign · docs/67).
 *
 * Ports the approved admin mockup: a horizontal top nav, not a sidebar.
 *   Left   — TACOSAI · Admin wordmark (the "AI" in indigo, matching the mockup)
 *   Centre — horizontal nav, lucide icon + label, active route in indigo
 *   Right  — theme toggle + user chip (Sign out via AuthContext.signOut)
 * Content: routed children fill the remainder, full width.
 *
 * Desktop-first; the nav scrolls horizontally on narrow viewports.
 */
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  CircleUser,
  FileText,
  LayoutDashboard,
  LogOut,
  Moon,
  Settings,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Sun,
  Users,
  Wallet,
  Workflow,
} from 'lucide-react';

import { useAuth } from '@/auth/AuthContext.js';
import { isFounder } from '@/auth/founder.js';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTheme } from '@/hooks/use-theme';
import { cn } from '@/lib/utils';

const NAV = [
  { to: '/users', label: 'Users', icon: Users },
  { to: '/payments', label: 'Payments', icon: Wallet },
  { to: '/gpu', label: 'GPU resources', icon: LayoutDashboard },
  { to: '/gpu/readiness', label: 'Pool readiness', icon: ShieldCheck },
  { to: '/workflows', label: 'ComfyUI templates', icon: Workflow },
  { to: '/loras', label: 'LoRAs', icon: Sparkles },
  { to: '/audit', label: 'Audit log', icon: FileText },
  { to: '/config', label: 'System config', icon: Settings },
  { to: '/moderation', label: 'Moderation', icon: ShieldAlert },
] as const;

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  const Icon = theme === 'dark' ? Moon : Sun;
  const label = theme === 'dark' ? 'Dark theme — click for light' : 'Light theme — click for dark';
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      aria-label={label}
      title={label}
    >
      <Icon className="h-4 w-4" />
    </Button>
  );
}

function UserChip() {
  const { user, signOut } = useAuth();
  if (!user) return null;
  const label = user.username ?? user.preferred_username ?? user.email ?? user.sub;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2">
          <CircleUser className="h-4 w-4" />
          <span className="text-sm">{label}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel className="font-normal text-muted-foreground">
          Signed in as
          <div className="mt-0.5 truncate text-sm font-medium text-foreground">
            {user.email ?? label}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            void signOut();
          }}
          className="text-destructive focus:bg-destructive/10 focus:text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  // LoRAs is founder-only — hide the tab for everyone else (route is also FounderGuard-wrapped).
  const items = NAV.filter((item) => item.to !== '/loras' || isFounder(user));
  // Most-specific match wins, so /gpu/readiness lights "Pool readiness", not "GPU resources".
  const activeTo = items
    .map((i) => i.to)
    .filter((to) => pathname === to || pathname.startsWith(to + '/'))
    .sort((a, b) => b.length - a.length)[0];
  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <header className="flex h-14 shrink-0 items-center gap-5 border-b bg-card px-6">
        <Link to="/" className="flex shrink-0 items-baseline gap-1.5 text-[15px] font-bold tracking-tight">
          <span>
            TACOS<span className="text-indigo-600 dark:text-indigo-400">AI</span>
          </span>
          <span className="font-normal text-muted-foreground">·</span>
          <span className="font-semibold">Admin</span>
        </Link>
        <nav className="flex min-w-0 items-center gap-0.5 overflow-x-auto">
          {items.map((item) => {
            const active = item.to === activeTo;
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  'flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-[13px] transition-colors',
                  active
                    ? 'bg-indigo-50 font-medium text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400'
                    : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <div className="ml-auto flex shrink-0 items-center gap-1">
          <ThemeToggle />
          <UserChip />
        </div>
      </header>
      <main className="flex-1 overflow-auto">{children}</main>
    </div>
  );
}
